"use client";

import type { ReactNode } from "react";
import { useEffect, useLayoutEffect } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useUI } from "@/lib/context/UIProvider";
import { useAuth } from "@/lib/context/AuthProvider";
import { SceneBackground } from "@/components/layout/SceneBackground";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { FloatingDock } from "@/components/layout/FloatingDock";
import {
  importAuthModal,
  importProModal,
} from "@/components/layout/modalPreload";
import { cn } from "@/lib/utils";

const AuthModal = dynamic(
  () => importAuthModal().then((m) => ({ default: m.AuthModal })),
  { ssr: false },
);
const ProModal = dynamic(
  () => importProModal().then((m) => ({ default: m.ProModal })),
  { ssr: false },
);

const AUTH_PROMPT_KEY = "nursetools_auth_prompt_shown";

/** `useLayoutEffect` that degrades to a no-op during SSR (avoids the warning). */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** Persistent app frame: background, drawer, header, dock, and content. */
export function AppShell({ children }: { children: ReactNode }) {
  const {
    sidebarOpen,
    closeSidebar,
    openAuthModal,
    isPageLoading,
    authModalOpen,
    proModalOpen,
  } = useUI();
  const { hydrated, isAuthenticated } = useAuth();
  const pathname = usePathname();
  // Tool detail shows its own bottom action bar on mobile, so hide the dock there.
  const isToolDetail = /^\/tools\/.+/.test(pathname);
  // Public / post-checkout flows must stay usable without a sign-in prompt.
  const isPublicCertificateVerify = pathname.startsWith("/certificates/verify");
  const isCheckoutReturn =
    pathname.startsWith("/checkout/") ||
    pathname.startsWith("/payment/checkout/");

  // Shared layout keeps the document scroll position across soft navigations.
  // Must run before paint — in a passive effect the new route paints at the
  // old offset first, which reads as the page jumping.
  useIsomorphicLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  // Flag the document while a modal is open so `globals.css` can switch off
  // the header/dock backdrop-filters that the modal scrim already covers.
  useEffect(() => {
    const root = document.documentElement;
    if (!authModalOpen && !proModalOpen) {
      root.removeAttribute("data-modal-open");
      return;
    }
    root.setAttribute("data-modal-open", "");
    return () => root.removeAttribute("data-modal-open");
  }, [authModalOpen, proModalOpen]);

  // Warm the modal chunks once the route has settled. They are `ssr: false`
  // dynamic imports, so without this the first click on a Pro tool card pays
  // for the chunk fetch before anything happens on screen. Loading the module
  // is inert — GSI/Apple only load when AuthModal actually mounts.
  useEffect(() => {
    if (isPageLoading) return;

    let idleId: number | undefined;
    const warm = () => {
      void importProModal();
      void importAuthModal();
    };

    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(warm, { timeout: 4000 });
    } else {
      const timer = window.setTimeout(warm, 1500);
      return () => window.clearTimeout(timer);
    }

    return () => {
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
    };
  }, [isPageLoading]);

  // Nudge signed-out visitors to create an account, once per browser tab
  // session — but only once we know for sure they're not already signed in.
  // Wait until the route finished loading, then idle, so GSI/Apple (~170 KiB)
  // don't contend with LCP / tools fetch.
  useEffect(() => {
    /* istanbul ignore next -- client component: `window` always exists when the effect runs */
    if (typeof window === "undefined") return;
    if (!hydrated || isAuthenticated || isPageLoading) return;
    if (isPublicCertificateVerify || isCheckoutReturn) return;
    if (window.sessionStorage.getItem(AUTH_PROMPT_KEY)) return;

    let idleId: number | undefined;
    const open = () => {
      /* istanbul ignore next -- second read of the flag, for the idle callback that may fire later */
      if (window.sessionStorage.getItem(AUTH_PROMPT_KEY)) return;
      window.sessionStorage.setItem(AUTH_PROMPT_KEY, "1");
      openAuthModal();
    };

    const timer = window.setTimeout(() => {
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(open, { timeout: 2500 });
      } else {
        open();
      }
    }, 1200);

    return () => {
      window.clearTimeout(timer);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
    };
  }, [
    hydrated,
    isAuthenticated,
    isPageLoading,
    isPublicCertificateVerify,
    isCheckoutReturn,
    openAuthModal,
  ]);

  return (
    <>
      <SceneBackground />

      {/* Scrim behind the drawer */}
      <div
        aria-hidden="true"
        onClick={closeSidebar}
        className={cn(
          "fixed inset-0 z-[65] cursor-pointer bg-[rgba(26,24,22,0.34)] transition-opacity duration-300",
          sidebarOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <Sidebar />

      {/* Bottom pad clears the fixed FloatingDock (not oversized — avoids phantom scroll). */}
      <div className="relative z-10 mx-auto min-h-screen max-w-[1200px] overflow-x-clip pb-[calc(100px_+_env(safe-area-inset-bottom))] md:pb-[78px]">
        <div
          className="transition-transform duration-[440ms] [transition-timing-function:cubic-bezier(.22,1,.36,1)]"
          style={{
            transform: sidebarOpen ? "var(--stage-shift)" : undefined,
          }}
        >
          <Header />
          <main>{children}</main>
        </div>
      </div>

      <FloatingDock hideOnMobile={isToolDetail} />
      {/* Mount on demand so auth/pro chunks + GSI/Apple stay off the critical path. */}
      {authModalOpen ? <AuthModal /> : null}
      {proModalOpen ? <ProModal /> : null}
    </>
  );
}
